import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Target, DollarSign, BarChart3, TrendingUp, ArrowRight, ArrowLeft, Calendar, CheckCircle, XCircle, Share2 } from "lucide-react";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { API } from "../App";
import Navbar, { AuthModal } from "../components/Navbar";
import TeamMatchup from "../components/TeamMatchup";
import WinShareCard from "../components/WinShareCard";
import HotStreaks from "../components/HotStreaks";
import CLVStats from "../components/CLVStats";
import axios from "axios";
import SEO from "../components/SEO";

const StatCard = ({ icon: Icon, label, value, subValue, color = "text-white" }) => (
  <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-4">
      <Icon className="w-6 h-6 text-hsl-primary" />
    </div>
    <p className="text-zinc-500 text-xs uppercase tracking-wide mb-1">{label}</p>
    <p className={`font-mono text-3xl md:text-4xl font-bold ${color}`}>{value}</p>
    {subValue && <p className="text-zinc-500 text-sm mt-1">{subValue}</p>}
  </div>
);

// Helper to calculate profit based on American odds
const calculatePickProfit = (pick) => {
  if (pick.result !== 'win' && pick.result !== 'loss') return 0;
  
  const betAmount = pick.bet_amount || 100;
  const oddsNum = parseInt(String(pick.odds || '-110').replace('+', ''));
  
  if (pick.result === 'win') {
    if (oddsNum > 0) {
      return betAmount * (oddsNum / 100);
    } else {
      return betAmount * (100 / Math.abs(oddsNum));
    }
  }
  return -betAmount;
};

// Format profit display
const formatProfit = (profit) => {
  if (profit >= 0) {
    return `+$${profit.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
  }
  return `-$${Math.abs(profit).toLocaleString(undefined, {maximumFractionDigits: 0})}`;
};

const SportBreakdown = ({ sportData, onSportClick, selectedSport }) => {
  if (!sportData || Object.keys(sportData).length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        No sport data available yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(sportData).map(([sport, data]) => {
        const winRate = data.total > 0 ? ((data.wins / (data.wins + data.losses)) * 100) || 0 : 0;
        const isSelected = selectedSport === sport;
        
        return (
          <motion.div 
            key={sport} 
            className={`rounded-lg p-4 cursor-pointer transition-all duration-200 ${
              isSelected 
                ? 'bg-hsl-primary/20 border-2 border-hsl-primary' 
                : 'bg-hsl-bg/50 border-2 border-transparent hover:border-hsl-primary/50 hover:bg-hsl-bg'
            }`}
            data-testid={`sport-breakdown-${sport.toLowerCase()}`}
            onClick={() => onSportClick(sport)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold uppercase text-white">{sport}</span>
                {isSelected && (
                  <span className="text-xs bg-hsl-primary text-black px-2 py-0.5 rounded-full font-bold">
                    SELECTED
                  </span>
                )}
              </div>
              <span className="font-mono text-sm text-zinc-400">
                {data.wins}-{data.losses}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Progress 
                value={winRate} 
                className="flex-1 h-2 bg-zinc-800"
              />
              <span className={`font-mono text-sm font-bold ${winRate >= 55 ? 'text-green-400' : winRate >= 50 ? 'text-hsl-primary' : 'text-red-400'}`}>
                {winRate.toFixed(1)}%
              </span>
            </div>
            <p className="text-zinc-500 text-xs mt-2">Click to view all {sport} picks →</p>
          </motion.div>
        );
      })}
    </div>
  );
};

// Component to display picks for a selected sport
const SportPicksList = ({ sport, onBack }) => {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sharePick, setSharePick] = useState(null);

  useEffect(() => {
    const fetchSportPicks = async () => {
      setLoading(true);
      try {
        // Fetch picks for this sport that are completed (not pending)
        const response = await axios.get(`${API}/picks`, { 
          params: { 
            sport: sport,
            status: 'completed',
            limit: 500
          } 
        });
        
        // Filter to only completed picks
        const completedPicks = response.data.filter(p => p.result !== 'pending');
        setPicks(completedPicks);
        
        // Calculate stats for this sport
        const wins = completedPicks.filter(p => p.result === 'win').length;
        const losses = completedPicks.filter(p => p.result === 'loss').length;
        const pushes = completedPicks.filter(p => p.result === 'push').length;
        const totalProfit = completedPicks.reduce((sum, p) => {
          if (p.result === 'win') {
            const odds = parseInt(p.odds);
            if (odds > 0) {
              return sum + (p.bet_amount * (odds / 100));
            } else {
              return sum + (p.bet_amount * (100 / Math.abs(odds)));
            }
          } else if (p.result === 'loss') {
            return sum - p.bet_amount;
          }
          return sum;
        }, 0);
        
        setStats({
          wins,
          losses,
          pushes,
          total: completedPicks.length,
          winRate: wins + losses > 0 ? ((wins / (wins + losses)) * 100).toFixed(1) : 0,
          profit: totalProfit
        });
      } catch (error) {
        console.error("Failed to fetch sport picks:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSportPicks();
  }, [sport]);

  if (loading) {
    return (
      <div className="space-y-4">
        {Array(5).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Header with back button */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-zinc-400 hover:text-white"
          data-testid="back-to-sports"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to All Sports
        </Button>
      </div>
      
      {/* Sport Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-hsl-bg/50 rounded-xl border border-white/10">
          <div className="text-center">
            <p className="text-zinc-500 text-xs uppercase">Record</p>
            <p className="font-mono text-xl font-bold text-white">{stats.wins}-{stats.losses}</p>
          </div>
          <div className="text-center">
            <p className="text-zinc-500 text-xs uppercase">Win Rate</p>
            <p className={`font-mono text-xl font-bold ${parseFloat(stats.winRate) >= 55 ? 'text-green-400' : parseFloat(stats.winRate) >= 50 ? 'text-hsl-primary' : 'text-red-400'}`}>
              {stats.winRate}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-zinc-500 text-xs uppercase">Profit</p>
            <p className={`font-mono text-xl font-bold ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {stats.profit >= 0 ? '+' : ''}${stats.profit.toFixed(0)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-zinc-500 text-xs uppercase">Total Picks</p>
            <p className="font-mono text-xl font-bold text-white">{stats.total}</p>
          </div>
        </div>
      )}
      
      {/* Picks List */}
      <div className="space-y-3">
        <h3 className="font-heading text-lg font-bold uppercase text-white">
          All {sport} Results ({picks.length})
        </h3>
        
        {picks.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            No completed picks for {sport} yet
          </div>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
            {picks.map((pick) => (
              <motion.div
                key={pick.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg border ${
                  pick.result === 'win' 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : pick.result === 'loss'
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-zinc-500/10 border-zinc-500/30'
                }`}
                data-testid={`pick-result-${pick.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {pick.result === 'win' ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : pick.result === 'loss' ? (
                        <XCircle className="w-4 h-4 text-red-400" />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-zinc-500" />
                      )}
                      <span className={`text-xs font-bold uppercase ${
                        pick.result === 'win' ? 'text-green-400' : pick.result === 'loss' ? 'text-red-400' : 'text-zinc-400'
                      }`}>
                        {pick.result}
                      </span>
                      {pick.pick_tier === 'hammer' && (
                        <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded font-bold">
                          🔨 HAMMER
                        </span>
                      )}
                      {/* Final Score Display */}
                      {(pick.away_score !== null && pick.away_score !== undefined && 
                        pick.home_score !== null && pick.home_score !== undefined) && (
                        <span className="text-xs bg-zinc-700/50 text-zinc-300 px-2 py-0.5 rounded font-mono">
                          {pick.away_score} - {pick.home_score}
                        </span>
                      )}
                      {/* Share Win Button */}
                      {pick.result === 'win' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setSharePick(pick); }}
                          className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded font-bold hover:bg-cyan-500/30 transition-colors flex items-center gap-1"
                          data-testid={`share-win-${pick.id}`}
                        >
                          <Share2 className="w-3 h-3" /> Share
                        </button>
                      )}
                    </div>
                    <p className="font-heading font-bold text-white uppercase text-sm">
                      <TeamMatchup matchup={pick.matchup} sport={pick.sport || pick.league} compact />
                    </p>
                    <p className="text-zinc-400 text-xs mt-1">
                      <span className="text-hsl-primary">{pick.pick}</span> • {pick.odds}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono font-bold ${
                      pick.result === 'win' ? 'text-green-400' : pick.result === 'loss' ? 'text-red-400' : 'text-zinc-400'
                    }`}>
                      {formatProfit(calculatePickProfit(pick))}
                    </p>
                    <p className="text-zinc-500 text-xs flex items-center gap-1 justify-end">
                      <Calendar className="w-3 h-3" />
                      {new Date(pick.game_time).toLocaleDateString('en-US', { 
                        timeZone: 'America/New_York',
                        month: 'short', 
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Share Win Modal */}
      {sharePick && (
        <WinShareCard pick={sharePick} onClose={() => setSharePick(null)} />
      )}
    </motion.div>
  );
};

// Last 20 Picks Performance by Sport Component
const Last20Performance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/stats/last-20-by-sport`);
        setData(response.data);
      } catch (error) {
        console.error("Failed to fetch last 20 stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="bg-hsl-surface border border-white/10 rounded-xl p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || !data.by_sport || Object.keys(data.by_sport).length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-hsl-surface to-hsl-surface/50 border border-white/10 rounded-xl p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white uppercase">Last 20 Per Sport</h2>
          <p className="text-zinc-500 text-sm">Each sport's last 20 graded picks with ROI</p>
        </div>
      </div>

      {/* Props Performance Card - Show first if props exist */}
      {data.props && data.props.picks > 0 && (
        <div className="mb-6">
          <div 
            className="bg-gradient-to-r from-purple-500/10 to-hsl-primary/10 rounded-lg p-4 border border-purple-500/30 relative overflow-hidden"
            data-testid="props-performance-card"
          >
            {/* Prop Specialist Badge - Shows when win rate >= 60% in last 20 */}
            {data.props.win_rate >= 60 && data.props.picks >= 5 && (
              <div className="absolute top-3 right-3" data-testid="prop-specialist-badge-last20">
                <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg shadow-purple-500/30">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  SPECIALIST
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-purple-400 uppercase text-lg">🎯 PROPS</span>
                <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-0.5 rounded">Player Props</span>
              </div>
              <span className="font-mono text-sm font-bold text-white bg-hsl-surface px-2 py-1 rounded">
                {data.props.wins}-{data.props.losses}
              </span>
            </div>
            <p className="text-zinc-500 text-xs mb-3">Last {data.props.picks} prop bets</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-hsl-surface/50 rounded p-2">
                <p className="text-zinc-500 text-xs">Win%</p>
                <p className={`font-mono text-lg font-bold ${data.props.win_rate >= 55 ? 'text-green-400' : data.props.win_rate >= 50 ? 'text-white' : 'text-red-400'}`}>
                  {data.props.win_rate}%
                </p>
              </div>
              <div className="bg-hsl-surface/50 rounded p-2">
                <p className="text-zinc-500 text-xs">Profit</p>
                <p className={`font-mono text-lg font-bold ${data.props.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {data.props.profit >= 0 ? '+' : ''}{data.props.profit >= 1000 || data.props.profit <= -1000 ? `$${(data.props.profit/1000).toFixed(1)}K` : `$${data.props.profit.toLocaleString()}`}
                </p>
              </div>
              <div className="bg-hsl-surface/50 rounded p-2">
                <p className="text-zinc-500 text-xs">ROI</p>
                <p className={`font-mono text-lg font-bold ${data.props.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {data.props.roi >= 0 ? '+' : ''}{data.props.roi}%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* By Sport Breakdown - Each showing their own last 20 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(data.by_sport).map(([sport, stats]) => (
          <div 
            key={sport} 
            className="bg-hsl-bg/50 rounded-lg p-4 border border-white/5 hover:border-hsl-primary/30 transition-colors"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading font-bold text-white uppercase text-lg">{sport}</span>
              <span className="font-mono text-sm font-bold text-white bg-hsl-surface px-2 py-1 rounded">
                {stats.wins}-{stats.losses}
              </span>
            </div>
            <p className="text-zinc-500 text-xs mb-3">Last {stats.picks} picks</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-hsl-surface/50 rounded p-2">
                <p className="text-zinc-500 text-xs">Win%</p>
                <p className={`font-mono text-lg font-bold ${stats.win_rate >= 55 ? 'text-green-400' : stats.win_rate >= 50 ? 'text-white' : 'text-red-400'}`}>
                  {stats.win_rate}%
                </p>
              </div>
              <div className="bg-hsl-surface/50 rounded p-2">
                <p className="text-zinc-500 text-xs">Profit</p>
                <p className={`font-mono text-lg font-bold ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {stats.profit >= 0 ? '+' : ''}{stats.profit >= 1000 || stats.profit <= -1000 ? `$${(stats.profit/1000).toFixed(1)}K` : `$${stats.profit.toLocaleString()}`}
                </p>
              </div>
              <div className="bg-hsl-surface/50 rounded p-2">
                <p className="text-zinc-500 text-xs">ROI</p>
                <p className={`font-mono text-lg font-bold ${stats.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {stats.roi >= 0 ? '+' : ''}{stats.roi}%
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrackRecordPage = () => {
  const [trackRecord, setTrackRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [selectedSport, setSelectedSport] = useState(null);

  useEffect(() => {
    const fetchTrackRecord = async () => {
      try {
        const response = await axios.get(`${API}/track-record`);
        setTrackRecord(response.data);
      } catch (error) {
        console.error("Failed to fetch track record:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrackRecord();
  }, []);

  const handleSportClick = (sport) => {
    setSelectedSport(sport);
  };

  const handleBackToSports = () => {
    setSelectedSport(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-hsl-bg">
        <Navbar 
          onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
          onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {Array(4).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const record = trackRecord || {
    total_picks: 0,
    wins: 0,
    losses: 0,
    pushes: 0,
    pending: 0,
    win_rate: 0,
    total_profit: 0,
    roi: 0,
    sport_breakdown: {}
  };

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="track-record-page">
      <Navbar 
        onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
        onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
      />
      
      <SEO 
        title="Track Record - Verified Results"
        description={`Verified sports betting track record: ${record.win_rate}% win rate, $${record.total_profit?.toLocaleString()} profit. Full transparency on every pick.`}
        keywords="sports betting track record, verified picks, win rate, sports handicapping results, betting history"
        canonical="/track-record"
      />
      
      {/* Header */}
      <div className="border-b border-white/5 bg-hsl-surface/50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white" data-testid="track-record-title">
                Track Record
              </h1>
              <p className="text-zinc-400 mt-2">Transparent results, verified performance</p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button className="btn-primary-glow">
                  View Today's Picks <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12"
        >
          <StatCard
            icon={Target}
            label="Season Record"
            value={`${record.wins}-${record.losses}`}
            subValue={record.pushes > 0 ? `${record.pushes} pushes` : undefined}
          />
          <StatCard
            icon={TrendingUp}
            label="Win Rate"
            value={`${record.win_rate}%`}
            color={record.win_rate >= 55 ? "text-green-400" : record.win_rate >= 50 ? "text-hsl-primary" : "text-red-400"}
          />
          <StatCard
            icon={DollarSign}
            label="Total Profit"
            value={`${record.total_profit >= 0 ? '+' : '-'}$${Math.abs(record.total_profit).toLocaleString()}`}
            color={record.total_profit >= 0 ? "text-green-400" : "text-red-400"}
          />
          <StatCard
            icon={BarChart3}
            label="ROI"
            value={`${record.roi}%`}
            color={record.roi >= 0 ? "text-hsl-secondary" : "text-red-400"}
          />
        </motion.div>

        {/* Hot Streaks */}
        <HotStreaks compact className="mb-8" />

        {/* CLV Stats */}
        <div className="mb-8">
          <CLVStats />
        </div>

        {/* Props vs Games Breakdown */}
        {record.prop_breakdown && (record.prop_breakdown.props?.total > 0 || record.prop_breakdown.games?.total > 0) && (
          <div className="bg-gradient-to-br from-hsl-surface to-hsl-surface/50 border border-white/10 rounded-xl p-6 mb-8" data-testid="prop-breakdown-section">
            <div className="mb-6">
              <h2 className="font-heading text-2xl font-bold text-white uppercase">Props vs Games</h2>
              <p className="text-zinc-500 text-sm">Performance breakdown by bet type</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Props Card */}
              {record.prop_breakdown.props?.total > 0 && (
                <div className="bg-gradient-to-r from-purple-500/10 to-hsl-primary/10 rounded-lg p-5 border border-purple-500/30 relative overflow-hidden" data-testid="all-props-card">
                  {/* Prop Specialist Badge - Shows when win rate >= 60% and at least 5 props */}
                  {record.prop_breakdown.props.win_rate >= 60 && record.prop_breakdown.props.total >= 5 && (
                    <div className="absolute top-3 right-3" data-testid="prop-specialist-badge">
                      <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg shadow-purple-500/30 animate-pulse">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        PROP SPECIALIST
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-purple-400 uppercase text-xl">🎯 PROPS</span>
                    </div>
                    <span className="font-mono text-lg font-bold text-white bg-hsl-surface px-3 py-1 rounded">
                      {record.prop_breakdown.props.wins}-{record.prop_breakdown.props.losses}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-sm mb-4">{record.prop_breakdown.props.total} total prop bets</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-hsl-surface/50 rounded p-3">
                      <p className="text-zinc-500 text-xs">Win%</p>
                      <p className={`font-mono text-xl font-bold ${record.prop_breakdown.props.win_rate >= 55 ? 'text-green-400' : record.prop_breakdown.props.win_rate >= 50 ? 'text-white' : 'text-red-400'}`}>
                        {record.prop_breakdown.props.win_rate}%
                      </p>
                    </div>
                    <div className="bg-hsl-surface/50 rounded p-3">
                      <p className="text-zinc-500 text-xs">Profit</p>
                      <p className={`font-mono text-xl font-bold ${record.prop_breakdown.props.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {record.prop_breakdown.props.profit >= 0 ? '+' : ''}${Math.abs(record.prop_breakdown.props.profit).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-hsl-surface/50 rounded p-3">
                      <p className="text-zinc-500 text-xs">ROI</p>
                      <p className={`font-mono text-xl font-bold ${record.prop_breakdown.props.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {record.prop_breakdown.props.roi >= 0 ? '+' : ''}{record.prop_breakdown.props.roi}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Games Card */}
              {record.prop_breakdown.games?.total > 0 && (
                <div className="bg-hsl-bg/50 rounded-lg p-5 border border-white/10" data-testid="all-games-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-hsl-primary uppercase text-xl">🏈 GAMES</span>
                    </div>
                    <span className="font-mono text-lg font-bold text-white bg-hsl-surface px-3 py-1 rounded">
                      {record.prop_breakdown.games.wins}-{record.prop_breakdown.games.losses}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-sm mb-4">{record.prop_breakdown.games.total} total game bets</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-hsl-surface/50 rounded p-3">
                      <p className="text-zinc-500 text-xs">Win%</p>
                      <p className={`font-mono text-xl font-bold ${record.prop_breakdown.games.win_rate >= 55 ? 'text-green-400' : record.prop_breakdown.games.win_rate >= 50 ? 'text-white' : 'text-red-400'}`}>
                        {record.prop_breakdown.games.win_rate}%
                      </p>
                    </div>
                    <div className="bg-hsl-surface/50 rounded p-3">
                      <p className="text-zinc-500 text-xs">Profit</p>
                      <p className={`font-mono text-xl font-bold ${record.prop_breakdown.games.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {record.prop_breakdown.games.profit >= 0 ? '+' : ''}${Math.abs(record.prop_breakdown.games.profit).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-hsl-surface/50 rounded p-3">
                      <p className="text-zinc-500 text-xs">ROI</p>
                      <p className={`font-mono text-xl font-bold ${record.prop_breakdown.games.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {record.prop_breakdown.games.roi >= 0 ? '+' : ''}{record.prop_breakdown.games.roi}%
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Last 20 Picks Performance */}
        <Last20Performance />

        {/* Detailed Stats */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Overview */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-hsl-surface border border-white/5 rounded-xl p-6 md:p-8"
            data-testid="overview-card"
          >
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">
              Season Overview
            </h2>
            
            <div className="space-y-6">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-zinc-400">Total Picks</span>
                  <span className="font-mono text-white font-bold">{record.total_picks}</span>
                </div>
                <Progress value={100} className="h-2 bg-zinc-800" />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-zinc-400">Wins</span>
                  <span className="font-mono text-green-400 font-bold">{record.wins}</span>
                </div>
                <Progress 
                  value={record.total_picks > 0 ? (record.wins / record.total_picks) * 100 : 0} 
                  className="h-2 bg-zinc-800"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-zinc-400">Losses</span>
                  <span className="font-mono text-red-400 font-bold">{record.losses}</span>
                </div>
                <Progress 
                  value={record.total_picks > 0 ? (record.losses / record.total_picks) * 100 : 0} 
                  className="h-2 bg-zinc-800"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-zinc-400">Pending</span>
                  <span className="font-mono text-yellow-400 font-bold">{record.pending}</span>
                </div>
                <Progress 
                  value={record.total_picks > 0 ? (record.pending / record.total_picks) * 100 : 0} 
                  className="h-2 bg-zinc-800"
                />
              </div>
            </div>

            <div className="mt-8 p-4 bg-hsl-bg/50 rounded-lg border border-white/5">
              <p className="text-zinc-500 text-sm">
                <span className="text-hsl-primary font-semibold">Note:</span> All picks are tracked with timestamps and cannot be altered. 
                Results are updated within 24 hours of game completion.
              </p>
            </div>
          </motion.div>

          {/* Sport Breakdown */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-hsl-surface border border-white/5 rounded-xl p-6 md:p-8"
            data-testid="sport-breakdown-card"
          >
            <AnimatePresence mode="wait">
              {selectedSport ? (
                <motion.div
                  key="sport-picks"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">
                    {selectedSport} Results
                  </h2>
                  <SportPicksList sport={selectedSport} onBack={handleBackToSports} />
                </motion.div>
              ) : (
                <motion.div
                  key="sport-list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">
                    By Sport
                  </h2>
                  <SportBreakdown 
                    sportData={record.sport_breakdown} 
                    onSportClick={handleSportClick}
                    selectedSport={selectedSport}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Methodology */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-hsl-surface border border-white/5 rounded-xl p-6 md:p-8"
          data-testid="methodology-card"
        >
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-6">
            Our Methodology
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-heading font-bold text-hsl-primary uppercase mb-2">Bet Sizing</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                We use a confidence-based scale from $100 to $1,500 per play. 
                $1,500 "Full Hammer" picks are our highest conviction plays.
              </p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-hsl-primary uppercase mb-2">Tracking</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                All picks are timestamped at release. We track against closing lines for accuracy. 
                No cherry-picking or post-game additions.
              </p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-hsl-primary uppercase mb-2">Transparency</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Every pick, win or loss, is documented and publicly visible. 
                We believe in full accountability.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default TrackRecordPage;
